import { Button, Modal } from 'antd';
import { RightOutlined } from '@ant-design/icons';
import SparkleSVG from '../../../assets/images/Sparkle';
import React, { useMemo, useRef } from 'react';
import { costString } from '../../../../../utils/cost';
import { useGetPlusPricesQuery } from '../../../proPricingSlice';
import '../../../assets/css/payment_form.css';
import '../../../assets/css/sb_plus_trial_modal.css';
import applePayMark from '../../../assets/images/Apple_Pay_Mark_RGB_041619.svg';
import googlePayMark from '../../../assets/images/google-pay-mark_800.svg';
import mastercardMark from '../../../assets/images/Mastercard.svg';
import visaMark from '../../../assets/images/Visa.svg';
import stripeLogo from '../../../assets/images/stripe-logo.svg';
import { usePreventScroll } from '../../../../../utils/hooks';
import appStoreIcon from '../../../../common/assets/images/Download_on_the_App_Store_Badge_US-UK_RGB_blk_092917.svg';
import googlePlayIcon from '../../../../common/assets/images/google-play-badge.png';
import FishBlueIcon from '../../../../../assets/images/img_blue_fish.png';
import giveawayMobileScreenshot from '../../../assets/images/giveawayMobileScreenshot.png';
import UserBookingNewAccountCreationForm from './UserBookingNewAccountCreationForm';
import ModalCloseCircle from '../../../assets/images/ModalCloseCircle';
import TrustpilotRating from '../../../../common/TrustpilotRating';

export const TrialTypes = {
  NEW_USER_TRIAL: 1, // Existing user has just been issued a free trial
  NEW_GUEST_TRIAL: 2, // Guest user will be offered a new trial once they finish signing up
  USER_TRIAL_IN_PROGRESS: 3, // User is currently within their free trial period
  USER_TRIAL_FINISHED: 4, // User previously had a trial, but has now finished
  PAID_SUBSCRIPTION: 5, // User is a paying subscriber
  GUEST_NO_TRIAL: 6, // Guest with no new trial (future-proofing in case trial is disabled)
  NO_TRIAL_OR_SUBSCRIPTION: null, // User has no subscription and has never had a trial (due to having an existing subscription when/before the trial system was added)
};

export function NewAccountCard({
  token,
  userId,
  bookingId,
  useTrialConfirmation = false,
  onAccountSetupFinish = () => {},
}) {
  return (
    <div
      className={'br20 txt-center overflow-hidden grey-900'}
      style={{ background: '#E9EFF3' }}
      id={'account-creation'}
    >
      <div className='uppercase gBold fs-18 pd-10 grey-100 sb-blue-bg-600'>
        Important Account Information
      </div>
      <div className={'pd-20'}>
        <UserBookingNewAccountCreationForm
          header={
            <p className='gRegular fs-17 lh-1.25'>
              Your account is almost ready - just add your password below and
              you'll be good to go!
            </p>
          }
          hideFieldLabels
          token={token}
          userId={userId}
          bookingId={bookingId}
          useTrialConfirmation={useTrialConfirmation}
          onFinish={onAccountSetupFinish}
        />
      </div>
    </div>
  );
}

function SBPlusTrialModal({
  open,
  onClose,
  type,
  userId,
  guestAccountProps = {},
}) {
  const { data: prices, isLoading: loading } = useGetPlusPricesQuery(
    undefined,
    { skip: type !== TrialTypes.USER_TRIAL_FINISHED }
  );
  const { proMonthlyPrice, discountedMonthlyPrice, discountedAnnualPrice } =
    useMemo(
      () => ({
        proMonthlyPrice: prices?.Pro?.find((p) => p.interval === 'month'),
        discountedMonthlyPrice: prices?.post_trial?.find(
          (p) => p.interval === 'month'
        ),
        discountedAnnualPrice: prices?.post_trial?.find(
          (p) => p.interval === 'year'
        ),
      }),
      [prices?.Pro, prices?.post_trial]
    );
  const annualDiscount =
    discountedAnnualPrice?.cost && discountedMonthlyPrice?.cost
      ? Math.round(
          ((proMonthlyPrice?.cost * 12 - discountedAnnualPrice?.cost) /
            (proMonthlyPrice?.cost * 12)) *
            100
        )
      : null;

  // Prevent background app scroll when Modal is open
  usePreventScroll('SBPlusTrialModal', open);

  const appLinks = (
    <>
      <div className='display-flex-row row-center gap-1r mg-top-10 mg-bottom-20'>
        <img
          alt='Swimbooker Logo: Icon of a fish on a blue background'
          width={45}
          height={45}
          src={FishBlueIcon}
        />
        <div className='display-flex-column'>
          <h3 className='gSemiBold sb-white fs-18 mg-bottom-0 lh-1'>
            swimbooker
          </h3>
          <p className='gRegular sb-white fs-9 mg-bottom-0 lh-1.1'>
            Less Hassle. More Fishing.
          </p>
        </div>
      </div>
      <div className='display-flex-row gap-1r justify-center w-full mg-top-10 mg-bottom-15 flex-wrap'>
        <a
          href={'https://apps.apple.com/gb/app/swimbooker/id1586855906'}
          target='_blank'
          rel='noopener noreferrer'
        >
          <img
            width={150}
            height={46}
            src={appStoreIcon}
            alt={'Download on the App Store'}
          />
        </a>
        <a
          href='https://play.google.com/store/apps/details?id=com.app.swimbookerapp'
          target='_blank'
          rel='noopener noreferrer'
        >
          <img width={150} src={googlePlayIcon} alt='Get it on Google Play' />
        </a>
      </div>
    </>
  );

  let title;
  let content;
  let buttonText;
  let buttonType = 'primary';
  switch (type) {
    case TrialTypes.NEW_USER_TRIAL:
    case TrialTypes.NEW_GUEST_TRIAL:
      title = "You've been upgraded!";
      content = (
        <>
          {/*<YouTube*/}
          {/*  videoId='FObk73Dnm4Y'*/}
          {/*  className='w-full h-full main-youtube mg-auto center cp ratio-16-9 br20 overflow-hidden mg-bottom-20'*/}
          {/*  style={{*/}
          {/*    display: 'grid',*/}
          {/*    maxWidth: 400,*/}
          {/*  }}*/}
          {/*  opts={{*/}
          {/*    height: '100%',*/}
          {/*    width: '100%',*/}
          {/*  }}*/}
          {/*/>*/}
          <p className='mg-top-10'>
            We've <strong>upgraded</strong> your swimbooker account to
            swimbooker <strong>PLUS</strong> for the next 7 days! You've now got{' '}
            <strong>access to all</strong> of our premium features including:
          </p>
          <ul className='white fs-20 lh-1.45 mg-bottom-25'>
            {[
              'Free Weekly Giveaways',
              'On-The Bank Insurance',
              'Advanced Catch Reports',
              'Cashback on ALL Bookings',
              'Discounted Fishing Gear',
              'Venue & Brand Perks',
            ].map((statement, i) => (
              <li key={`reward-${i}`}>{statement}</li>
            ))}
            <li>
              <strong className='font-montserrat'>Plus</strong> so much more!
            </li>
          </ul>
          <TrustpilotRating
            className='sb-blue-100 txt-center'
            inline
            linkClassName='seagreen-300 txt-underline hover:seagreen-500'
          />
          <div className='mg-bottom-25' />
          {type === TrialTypes.NEW_GUEST_TRIAL && (
            <NewAccountCard
              userId={userId}
              {...guestAccountProps}
              useTrialConfirmation={true}
            />
          )}
          <p className='fs-18 lh-1.33 mg-top-25 mg-bottom-30'>
            So, what's next? <strong>Download our app</strong> for the best
            possible experience - and to take advantage of your shiny, new, PLUS
            membership!
          </p>
          {appLinks}
          <p className='fs-18 lh-1.33 txt-center mg-top-25'>
            Pssst... there is a <strong>completely FREE to enter</strong>{' '}
            giveaway taking place this week. Once you've logged into the app
            visit the giveaway section to enter!
          </p>
          <img
            className='display-block w-full mg-auto-lr mg-bottom-10'
            style={{ maxWidth: 340 }}
            width={400}
            src={giveawayMobileScreenshot}
            alt={
              "Screenshot of swimbooker mobile app with the 'Giveaways' section in the 'Plus' tab highlighted."
            }
          />
        </>
      );
      buttonText = "Thanks, I'll check it out";
      break;

    case TrialTypes.USER_TRIAL_IN_PROGRESS:
      title = "Don't forget you've been upgraded!";
      content = (
        <>
          <p className='fs-14 lh-1.45 txt-center'>
            "I signed up for their subscription package which is an absolute
            game changer. (Affordable too) Not only do you get to book all your
            fishing but you can go fishing, knowing that you and your fishing
            gear are insured (with your subscription). They also have fantastic
            FREE mega prize giveaways every week, as well as perks, offering
            discounts on certain companies."
          </p>
          <p className='fs-14 lh-1.45 txt-center mg-bottom-20'>
            <strong> - Danny Mills</strong>
          </p>
          <TrustpilotRating
            className='sb-blue-100 txt-center'
            inline
            linkClassName='seagreen-300 txt-underline hover:seagreen-500'
          />
          <p className='mg-top-30'>
            Your swimbooker <strong>PLUS</strong> upgrade is still active!
            Follow the steps below to access all of your premium features!
          </p>
          <h2 className='mg-top-30'>HOW TO ACCESS</h2>
          <p className='fs-18 lh-1.33 mg-top-20 mg-bottom-30'>
            Easy! Simply <strong>Download our app</strong> from your mobile
            device's app store and log into your existing swimbooker account!
            Once in the app, click on the 'Plus' section.
          </p>
          {appLinks}
          <div className='mg-bottom-40' />
        </>
      );
      buttonText = "Thanks, I'll check it out";
      break;

    case TrialTypes.USER_TRIAL_FINISHED:
      title = 'Claim your lifetime discount';
      content = (
        <>
          <p>
            Thanks for trying <strong>PLUS</strong>! Extend your access to enter
            more weekly giveaways and{' '}
            <strong>protect yourself and your gear</strong> on the bank!
          </p>
          <h2 className='fs-16 mg-top-20 mg-bottom-15'>
            Your exclusive after trial offer
          </h2>
          <div className='mg-bottom-10 display-flex-row flex-wrap row-center justify-center gap-1r row-gap-0 font-montserrat fs-26 white'>
            <span
              className='fs-24'
              style={{
                textDecorationLine: 'line-through',
                textDecorationColor: '#8ABCE5',
                textDecorationThickness: 2,
              }}
            >
              &nbsp;
              {costString(
                proMonthlyPrice?.cost ? proMonthlyPrice.cost / 100.0 : null
              )}
              &nbsp;p/m&nbsp;
            </span>
            <span>
              {costString(
                discountedMonthlyPrice?.cost
                  ? discountedMonthlyPrice.cost / 100.0
                  : null
              )}
              &nbsp;p/m
              <span className='brFull fs-12 pd-left-10 pd-right-10 border-1 uppercase mg-left-12 va-middle'>
                your offer
              </span>
            </span>
          </div>
          <div className='bg-bright-gradient br16 pd-2 max-w-400 mg-auto-lr'>
            <Button
              href={`${process.env.REACT_APP_SBPLUS_URL}/sign-up/post-trial-offer?interval=month&userId=${userId}`}
              className='h-full w-full display-flex-row row-center txt-wrap br14 border-0 bg-black gRefular fs-18 white pd-15 antd-link-button-override'
            >
              <div className='display-flex-row row-center justify-center flex-grow-1 mg-top-5'>
                <SparkleSVG className='mg-right-20 fs-30' />
                <span className='lh-0'>
                  Join{' '}
                  <strong className='font-montserrat uppercase'>Plus</strong>{' '}
                  Now!
                </span>
                <RightOutlined className='mg-left-auto' />
              </div>
            </Button>
          </div>
          <p className='white txt-center fs-14 mg-top-20 mg-bottom-20'>
            -- OR TRY ANNUAL --
          </p>
          <a
            className='display-block gBold white txt-underline mg-bottom-30 txt-center'
            href={`${process.env.REACT_APP_SBPLUS_URL}/sign-up/post-trial-offer?interval=year&userId=${userId}`}
          >
            Join PLUS Annual for{' '}
            {costString(
              discountedAnnualPrice?.cost
                ? discountedAnnualPrice.cost / 100.0
                : null
            )}{' '}
            ({annualDiscount ?? 30}% Discount)
          </a>
          <div className='payment_option_marks payment_option_marks--no-background w-full mg-bottom-15'>
            <img src={applePayMark} alt={'Apple Pay Logo'} />
            <img src={googlePayMark} alt={'Google Pay Logo'} />
            <img src={mastercardMark} alt={'Mastercard Logo'} />
            <img src={visaMark} alt={'Visa Logo'} />
          </div>
          <p className='mg-bottom-30 txt-center grey-300 fs-14'>
            Securely processed via{' '}
            <img
              src={stripeLogo}
              alt='Stripe'
              className='display-inline-block h-30 mg-left-0'
            />
          </p>
          <TrustpilotRating
            className='sb-blue-100 txt-center'
            inline
            linkClassName='seagreen-300 txt-underline hover:seagreen-500'
          />
          <div className='mg-bottom-40' />
        </>
      );
      buttonText = 'Not right now, Thanks!';
      buttonType = 'secondary';
      break;

    default:
      title = null;
      content = null;
  }

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={'min(95%, 36rem)'}
      title={null}
      footer={null}
      closable={true}
      closeIcon={<ModalCloseCircle className='fs-45' />}
      maskClosable
      centered
      className='sb-trial-modal'
    >
      <div className='sb-trial-modal-content'>
        {title && <h1>{title}</h1>}
        {content}
        {buttonText && (
          <Button
            type={buttonType}
            className='trial-modal-button'
            onClick={onClose}
          >
            {buttonText}
          </Button>
        )}
      </div>
    </Modal>
  );
}

export default SBPlusTrialModal;
